import { useQuery } from "@tanstack/react-query";
import { getOrderList } from "src/api/booksOrder";
import { getPackage } from "src/api/package";
import { getCashProductById } from "src/api/wallet";
import { getSubscriptionPlan } from "src/api/subscription";
import { getPassOneProduct } from "../../../api/membership";
export const MAX_INT = 2147483647;
export const checkMaxInt = (value)=>{
    // value가 undefined이거나 빈 문자열인 경우 false 반환
    if (!value || value.trim() === "") {
        return false;
    }
    try {
        // value가 MAX_INT보다 크지 않은 경우에만 true (안전한 범위)
        const bigIntValue = BigInt(value);
        // 음수는 false 반환
        if (bigIntValue < 0) {
            return false;
        }
        return bigIntValue <= BigInt(MAX_INT);
    } catch (error) {
        ;
        return false;
    }
};
// 커스텀 에러 타입
export class PaymentQueryError extends Error {
    constructor(message, orderType, orderId, originalError){
        super(message);
        this.orderType = orderType;
        this.orderId = orderId;
        this.originalError = originalError;
        this.name = "PaymentQueryError";
    }
}
// 주문 타입별로 다른 API 호출
const getPaymentByType = async (orderType, orderId)=>{
    if (!orderId) {
        throw new PaymentQueryError("Order ID is required for payment data", orderType, orderId || "undefined");
    }
    // orderId 변환 헬퍼 함수
    const getSafeOrderId = (id)=>{
        if (checkMaxInt(id)) {
            return Number(BigInt(id));
        }
        ;
        return null;
    };
    try {
        switch(orderType){
            case "delivery":
            case "separate":
            case "upgrade":
                // separate 관련 결제 정보 조회
                return await getOrderList({
                    id: orderId
                });
            case "pass":
                // pass 관련 결제 정보 조회
                const passOrderId = getSafeOrderId(orderId);
                if (passOrderId !== null) {
                    return await getPassOneProduct(passOrderId);
                }
                return null;
            case "package":
                // package 관련 결제 정보 조회
                const packageOrderId = getSafeOrderId(orderId);
                if (packageOrderId !== null) {
                    return await getPackage(packageOrderId);
                }
                return null;
            case "pod":
                if (orderId !== null) {
                    return await getOrderList({
                        id: orderId
                    });
                }
                return null;
            case "cash":
                if (orderId !== null) {
                    return await getCashProductById(Number(orderId));
                }
                return null;
            case "subscription":
                // planId는 문자열이라 다른 타입처럼 숫자로 좁히지 않는다 (getSafeOrderId 미적용)
                return await getSubscriptionPlan(orderId);
            default:
                throw new PaymentQueryError("Unknown order type: ".concat(orderType), orderType, orderId || "undefined");
        }
    } catch (error) {
        if (error instanceof PaymentQueryError) {
            throw error;
        }
        throw new PaymentQueryError("Failed to fetch payment data: ".concat(error instanceof Error ? error.message : "Unknown error"), orderType, orderId || "undefined", error instanceof Error ? error : undefined);
    }
};
const usePaymentQuery = (param)=>{
    let { orderType , orderId  } = param;
    return useQuery({
        queryKey: [
            "payment",
            orderType,
            orderId
        ],
        queryFn: ()=>getPaymentByType(orderType, orderId),
        enabled: !!orderType && !!orderId,
        retry: (failureCount, error)=>{
            if (error.message.includes("required for payment data") || error.message.includes("Unknown order type") || error.message.includes("out of range for type integer") // 추가
            ) {
                return false;
            }
            return failureCount < 3;
        },
        retryDelay: (attemptIndex)=>Math.min(1000 * 2 ** attemptIndex, 30000),
        staleTime: 0,
        cacheTime: 0
    });
};
export default usePaymentQuery;
